import { html, nothing } from "lit";
import type { UiSettings } from "../storage.ts";
import type { PmosConnectorsStatus } from "../controllers/pmos-connectors.ts";
import type { PmosExecutionTraceEvent } from "../controllers/pmos-trace.ts";
import type { AgentsListResult, AgentIdentityResult } from "../types.ts";
import type { AgentActivitySummary } from "./agents.ts";
import type { ChatProps } from "./chat.ts";
import { renderChat } from "./chat.ts";
import { formatRelativeTimestamp } from "../format.ts";

function getTimeGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

export type DashboardProps = {
  connected: boolean;
  settings: UiSettings;
  lastError: string | null;
  connectorsLoading: boolean;
  connectorsError: string | null;
  connectorsStatus: PmosConnectorsStatus | null;
  projectId?: string;
  flowsLoading?: boolean;
  flowsError?: string | null;
  flows?: Array<{ id?: string; displayName?: string; status?: unknown }>;
  runsLoading?: boolean;
  runsError?: string | null;
  runs?: Array<{ id?: string; flowId?: string; status?: unknown; created?: unknown }>;
  traceEvents: PmosExecutionTraceEvent[];
  integrationsHref: string;
  automationsHref: string;
  chatHref: string;
  configHref?: string;
  modelAuthConfigured?: boolean;
  currentModel?: string;  // Current model being used (e.g., "google/gemini-3-flash-preview")
  currentModelProvider?: string;  // Provider of current model

  // workflow provisioning UI state (workspace-scoped Flow project + credentials)
  opsProvisioning?: boolean;
  opsProvisioned?: boolean;
  opsProvisioningResult?: { projectId?: string; apiKey?: string } | null;
  opsProvisioningError?: string | null;
  opsManualApiKeyDraft?: string;
  onOpsManualApiKeyChange?: (next: string) => void;
  onSaveOpsApiKey?: () => Promise<void>;

  onNavigateTab: (tab: "integrations" | "automations" | "chat" | "config" | "agents" | "command-center") => void;
  onSettingsChange: (next: UiSettings) => void;
  onConnect: () => void;
  onRefreshConnectors: () => void;
  onRefreshDashboard: () => void;
  onClearTrace: () => void;
  onProvisionOps?: () => Promise<void>;
  // Quick actions
  onQuickAction: (action: "check-leads" | "daily-report" | "create-workflow" | "settings") => void;

  // Agent system integration
  agentsList: AgentsListResult | null;
  agentActivityById: Record<string, AgentActivitySummary>;
  agentIdentityById: Record<string, AgentIdentityResult>;
  onOpenAgentChat: (agentId: string, opts?: { newSession?: boolean }) => void;
  // Inline chat panel
  chatProps: ChatProps;
  // Dashboard tab state
  dashboardTab: "home" | "agents" | "workflows" | "system";
  onDashboardTabChange: (tab: "home" | "agents" | "workflows" | "system") => void;
};

function renderStatusPill(label: string, value: string, status: "ok" | "warn") {
  return html`
    <div class="pill">
      <span class="statusDot ${status === "ok" ? "ok" : ""}"></span>
      <span>${label}</span>
      <span class="mono">${value}</span>
    </div>
  `;
}

function runStatusBucket(statusRaw: string) {
  const status = statusRaw.toUpperCase();
  if (status.includes("FAIL")) return "failed";
  if (status.includes("SUCCESS") || status.includes("SUCCEEDED")) return "succeeded";
  if (status.includes("RUNNING") || status.includes("EXECUTING") || status.includes("IN_PROGRESS")) {
    return "running";
  }
  return "other";
}

function traceStatusClass(status: PmosExecutionTraceEvent["status"]) {
  if (status === "success") return "chip chip-ok";
  if (status === "error") return "chip chip-danger";
  if (status === "running") return "chip chip-warn";
  return "chip";
}

function relativeFromAny(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    return formatRelativeTimestamp(value);
  }
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) {
      return formatRelativeTimestamp(parsed);
    }
  }
  return "n/a";
}

type SetupStep = {
  id: string;
  title: string;
  detail: string;
  done: boolean;
  href?: string;
  actionLabel?: string;
  actionKind?: "connect" | "refresh";
};

export function renderDashboard(props: DashboardProps) {
  const bcgpt = props.connectorsStatus?.bcgpt ?? null;
  const ops = props.connectorsStatus?.ops ?? null;
  const checkedAt = props.connectorsStatus?.checkedAtMs ?? null;
  const checkedLabel = checkedAt ? formatRelativeTimestamp(checkedAt) : "n/a";

  // Agent system
  const agents = props.agentsList?.agents ?? [];

  const bcgptStatus = (() => {
    if (!bcgpt) return { label: "Unknown", tone: "warn" as const };
    if (!bcgpt.configured) return { label: "Not connected", tone: "warn" as const };
    if (bcgpt.reachable === false || bcgpt.authOk === false) return { label: "Needs attention", tone: "warn" as const };
    if (bcgpt.reachable === true && (bcgpt.authOk === true || bcgpt.authOk === null)) return { label: "OK", tone: "ok" as const };
    return { label: "Checking", tone: "warn" as const };
  })();
  const opsRuntimeStatus = (() => {
    if (!ops) return { label: "Unknown", tone: "warn" as const };
    if (ops.reachable === true) return { label: "Ready", tone: "ok" as const };
    if (ops.reachable === false) return { label: "Offline", tone: "warn" as const };
    if (ops.configured) return { label: "Starting", tone: "warn" as const };
    return { label: "Not configured", tone: "warn" as const };
  })();

  const showAccessCard = !props.connected;
  const flows = props.flows ?? [];
  const runs = props.runs ?? [];
  const trace = props.traceEvents ?? [];
  const enabledFlows = flows.filter((flow) => String(flow.status ?? "").toUpperCase() === "ENABLED").length;

  const runBuckets = runs.reduce(
    (acc, run) => {
      const key = runStatusBucket(String(run.status ?? ""));
      acc[key] += 1;
      return acc;
    },
    { succeeded: 0, failed: 0, running: 0, other: 0 },
  );

  const pulse = (() => {
    if (!props.opsProvisioned) return { label: "Provisioning workflows...", tone: "warn" as const };
    if (opsRuntimeStatus.tone !== "ok") return { label: "Workflow runtime issue", tone: "warn" as const };
    if (bcgptStatus.tone !== "ok") return { label: "Connector risk", tone: "warn" as const };
    if (runBuckets.failed > 0) return { label: "Failures need review", tone: "warn" as const };
    return { label: "Healthy", tone: "ok" as const };
  })();

  const refreshBusy = props.connectorsLoading ?? false;
  // Core setup: only the 3 steps needed to start using the app.
  // BCGPT / AI model / first workflow are optional config â€” shown in Integrations tab.
  const setupSteps: SetupStep[] = [
    {
      id: "gateway",
      title: "Sign in",
      detail: "Authenticated and connected to your workspace.",
      done: props.connected,
      actionKind: "connect",
      actionLabel: "Reconnect",
    },
    {
      id: "ops",
      title: "Workflow engine ready",
      detail: "Your embedded workflow runtime is running. No provisioning needed.",
      done: Boolean(props.opsProvisioned),
      actionKind: "refresh",
      actionLabel: "Check status",
    },
    {
      id: "ops-runtime",
      title: "Workflows editor live",
      detail: "The workflow editor is embedded and accessible in the Workflows tab.",
      done: opsRuntimeStatus.tone === "ok",
      actionKind: "refresh",
      actionLabel: "Check runtime",
    },
  ];
  const setupCompleted = setupSteps.filter((step) => step.done).length;
  const allSetupDone = setupCompleted === setupSteps.length;
  const nextSetupStep = setupSteps.find((step) => !step.done) ?? null;
  // Collapse wizard automatically once core is ready (connected + workflow engine up).
  // Only keep it open if something is broken, so it doesn't distract on every login.
  const coreReady = props.connected && Boolean(props.opsProvisioned) && opsRuntimeStatus.tone === "ok";
  const wizardOpen = !coreReady && !allSetupDone;
  const focusItems = [
    bcgptStatus.tone !== "ok"
      ? { title: "Fix Basecamp connector", detail: "Restore MCP auth for project actions", href: props.integrationsHref }
      : null,
    runBuckets.failed > 0
      ? { title: "Check failed workflow runs", detail: `${runBuckets.failed} failed recent runs - review inside the Workflows editor`, href: props.automationsHref }
      : null,
    flows.length === 0
      ? { title: "Create first automation", detail: "Build a flow in Automations", href: props.automationsHref }
      : null,
  ].filter((item): item is { title: string; detail: string; href: string } => Boolean(item));

  const tab = props.dashboardTab ?? "home";

  const renderTabContent = () => {
    if (tab === "home") {
      return html`
        <div class="card">
          <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;justify-content:space-between;">
            <div>
              <div class="card-title">System Pulse</div>
              <span class="chip ${pulse.tone === "ok" ? "chip-ok" : "chip-warn"}">${pulse.label}</span>
            </div>
            <div class="row" style="gap:8px;">
              <button class="btn btn--sm" @click=${() => props.onNavigateTab("chat")}>Daily Brief</button>
              <button class="btn btn--sm" @click=${() => props.onNavigateTab("automations")}>Check Workflows</button>
              <button class="btn btn--sm" @click=${() => props.onNavigateTab("command-center" as Parameters<typeof props.onNavigateTab>[0])}>Open Projects</button>
            </div>
          </div>
        </div>

        ${focusItems.length > 0 ? html`
          <div class="card">
            <div class="card-title">Focus Today</div>
            <div class="card-sub">Prioritized actions to keep operations healthy.</div>
            <div class="list" style="margin-top: 12px;">
              ${focusItems.slice(0, 5).map(
                (item) => html`
                  <div class="list-item">
                    <div class="list-main">
                      <div class="list-title">${item.title}</div>
                      <div class="list-sub">${item.detail}</div>
                    </div>
                    <div class="list-meta">
                      <button class="btn btn--sm" @click=${() => {
                        if (item.href === props.integrationsHref) { props.onNavigateTab("integrations"); return; }
                        if (item.href === props.automationsHref) { props.onNavigateTab("automations"); return; }
                        props.onNavigateTab("chat");
                      }}>Open</button>
                    </div>
                  </div>
                `,
              )}
            </div>
          </div>
        ` : nothing}
      `;
    }

    if (tab === "agents") {
      return html`
        <section class="card">
          <div class="row" style="justify-content: space-between; align-items: center;">
            <div>
              <div class="card-title">Your AI Team</div>
              <div class="card-sub">${agents.length} agent${agents.length !== 1 ? 's' : ''} ready to help</div>
            </div>
            <button class="btn btn--sm" @click=${() => props.onNavigateTab("agents")}>
              Manage Agents
            </button>
          </div>
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; margin-top: 16px;">
            ${
              agents.length === 0
                ? html`
                  <div class="muted" style="grid-column: 1 / -1; text-align: center; padding: 32px;">
                    <div style="margin-bottom: 12px;">No agents configured yet.</div>
                    <button class="btn btn--primary" @click=${() => props.onNavigateTab("agents")}>
                      Create your first agent
                    </button>
                  </div>
                `
                : agents.map((agent) => {
                  const activity = props.agentActivityById[agent.id] ?? {
                    tasksRunning: 0, tasksQueued: 0, lastActivityAt: null, status: 'idle',
                  };
                  const identity = props.agentIdentityById[agent.id];
                  const displayName = agent.name?.trim() || agent.identity?.name?.trim() || identity?.name?.trim() || agent.id;
                  const emoji = identity?.emoji?.trim() || agent.identity?.emoji?.trim() || '\uD83E\uDD16';
                  const theme = agent.identity?.theme?.trim() || 'AI Agent';
                  const statusLabel = activity.status === 'active' ? 'Active' : activity.status === 'paused' ? 'Paused' : activity.status === 'error' ? 'Error' : 'Ready';
                  const statusClass = activity.status === 'active' ? 'chip-ok' : activity.status === 'paused' ? 'chip-warn' : activity.status === 'error' ? 'chip-danger' : '';
                  const taskCount = activity.tasksRunning + activity.tasksQueued;
                  return html`
                    <div class="card" style="padding: 16px;">
                      <div style="font-weight: 600;">${emoji} ${displayName}</div>
                      <div class="muted">${theme}</div>
                      <div style="margin-top: 8px;">
                        <span class="chip ${statusClass}">${statusLabel}</span>
                        ${taskCount > 0 ? html`<span class="muted">${taskCount} task${taskCount !== 1 ? 's' : ''}</span>` : nothing}
                      </div>
                      <div class="row" style="gap: 8px; margin-top: 8px;">
                        <button class="btn btn--sm btn--primary" @click=${() => props.onOpenAgentChat(agent.id)}>Chat</button>
                        <button class="btn btn--sm" @click=${() => props.onNavigateTab("agents")}>Settings</button>
                      </div>
                    </div>
                  `;
                })
            }
          </div>
        </section>
      `;
    }

    // "system" tab
    return html`
      <section class="grid grid-cols-2">
        <div class="card">
          <div class="card-title">Integration Health</div>
          <div class="card-sub">Connector state for Workflows and Basecamp.</div>
          <div class="stat-grid" style="margin-top: 16px;">
            <div class="stat">
              <div class="stat-label">Workflows</div>
              <div class="stat-value ${props.opsProvisioned && opsRuntimeStatus.tone === "ok" ? "ok" : "warn"}">
                ${props.opsProvisioned ? opsRuntimeStatus.label : "Pending"}
              </div>
              ${ops?.error ? html`<div class="muted">${ops.error}</div>` : nothing}
            </div>
            <div class="stat">
              <div class="stat-label">Basecamp</div>
              <div class="stat-value ${bcgptStatus.tone === "ok" ? "ok" : "warn"}">${bcgptStatus.label}</div>
              <div class="muted mono">${bcgpt?.url ?? "https://bcgpt.wickedlab.io"}</div>
            </div>
          </div>
          <div class="row" style="margin-top: 14px;">
            <button class="btn" ?disabled=${props.connectorsLoading || !props.connected} @click=${() => props.onRefreshConnectors()}>
              ${props.connectorsLoading ? "Checking..." : "Refresh status"}
            </button>
            <button class="btn btn--secondary" @click=${() => props.onNavigateTab("integrations")}>Open integrations</button>
            <span class="muted">Last check: ${checkedLabel}</span>
          </div>
          ${props.connectorsError ? html`<div class="callout danger" style="margin-top: 14px;">${props.connectorsError}</div>` : nothing}
        </div>

        <div class="card">
          <div class="card-title">System</div>
          <div class="card-sub">Wicked OS access and gateway connection.</div>
          ${showAccessCard
            ? html`
                <div class="form-grid" style="margin-top: 16px;">
                  <label class="field">
                    <span>Wicked OS Access Key (optional)</span>
                    <input type="password" .value=${props.settings.token}
                      @input=${(e: Event) => { const token = (e.target as HTMLInputElement).value; props.onSettingsChange({ ...props.settings, token }); }}
                      placeholder="Paste your access key" autocomplete="off" />
                  </label>
                </div>
                <div class="row" style="margin-top: 14px;">
                  <button class="btn" @click=${() => props.onConnect()} ?disabled=${props.connected}>Connect</button>
                  <span class="muted">Needed only for legacy/manual gateway access.</span>
                </div>
              `
            : html`
                <div style="margin-top: 16px; display: grid; gap: 8px;">
                  ${renderStatusPill("Gateway", props.connected ? "Connected" : "Offline", props.connected ? "ok" : "warn")}
                  ${renderStatusPill("Workflows", props.opsProvisioned ? "Provisioned" : "Pending", props.opsProvisioned ? "ok" : "warn")}
                </div>
              `}
          ${!props.opsProvisioned ? html`<div class="callout" style="margin-top: 14px;">Workflow workspace is provisioning - workflows will be available shortly.</div>` : nothing}
          ${props.lastError ? html`<div class="callout danger" style="margin-top: 14px;">${props.lastError}</div>` : nothing}
        </div>
      </section>

      <details class="card setup-wizard" style="" ?open=${wizardOpen}>
        <summary class="setup-wizard__summary">
          <div class="setup-wizard__summary-main">
            <div class="card-title">System Status</div>
            <div class="card-sub">${coreReady ? "Everything is running. Click to expand for details." : "Check that core services are running before using the app."}</div>
          </div>
          <span class="chip ${coreReady ? "chip-ok" : "chip-warn"}">${coreReady ? "Ready" : `${setupCompleted}/${setupSteps.length}`}</span>
        </summary>
        <div class="setup-wizard__body">
          <div class="row" style="margin-top: 12px; align-items: center;">
            <button class="btn btn--secondary" @click=${() => props.onRefreshDashboard()} ?disabled=${refreshBusy || !props.connected}>
              ${refreshBusy ? "Checking..." : "Run setup check"}
            </button>
            ${nextSetupStep
              ? html`<span class="muted">Next: <strong>${nextSetupStep.title}</strong></span>`
              : html`<span class="muted">Setup complete. <a href=${props.chatHref} class="btn btn--primary" style="margin-left:8px;">Start Using Chat</a></span>`}
          </div>
          ${props.opsProvisioningResult && props.opsProvisioningResult.apiKey
            ? html`<div class="callout success" style="margin-top:12px;">
                <div><strong>Workflow project provisioned</strong></div>
                <div style="margin-top:6px;">Project ID: <code>${props.opsProvisioningResult.projectId ?? "(n/a)"}</code></div>
                <div style="margin-top:6px;">API key: <code class="mono">${props.opsProvisioningResult.apiKey}</code></div>
                <div style="margin-top:8px;">
                  <button class="btn btn--secondary" @click=${() => navigator.clipboard?.writeText(props.opsProvisioningResult?.apiKey ?? "")}>Copy API key</button>
                  <button class="btn" @click=${() => props.onNavigateTab("integrations")}>Open integrations</button>
                </div>
              </div>`
            : nothing}
          ${props.opsProvisioningError
            ? html`<div class="callout warn" style="margin-top:12px;">
                <div><strong>Automated provisioning failed</strong></div>
                <div style="margin-top:6px;">${props.opsProvisioningError}</div>
              </div>
              <div class="form-grid" style="margin-top:8px;">
                <label class="field">
                  <span>Workflow API key</span>
                  <input type="password" .value=${props.opsManualApiKeyDraft ?? ""}
                    @input=${(e: Event) => props.onOpsManualApiKeyChange?.((e.target as HTMLInputElement).value)}
                    placeholder="Paste API key here" autocomplete="off" />
                </label>
              </div>
              <div class="row" style="margin-top:8px;">
                <button class="btn" @click=${() => props.onSaveOpsApiKey?.()} ?disabled=${!props.opsManualApiKeyDraft}>Save API key</button>
                <button class="btn btn--secondary" @click=${() => props.onNavigateTab("integrations")}>Open integrations</button>
              </div>`
            : nothing}
          <div style="margin-top:12px;">
            ${setupSteps.map((step) => html`
              <div class="setup-step" style="display:flex;align-items:center;justify-content:space-between;margin-top:12px;">
                <div>
                  <div style="font-weight:600">${step.title}</div>
                  <div class="muted" style="margin-top:4px">${step.detail}</div>
                </div>
                <div style="display:flex;gap:8px;align-items:center;">
                  ${step.done ? html`<span class="chip chip-ok">Done</span>` : html`<span class="chip">Pending</span>`}
                  ${step.actionKind === "connect"
                    ? html`<button class="btn btn--sm" @click=${() => props.onConnect()} ?disabled=${props.connected}>${step.actionLabel ?? "Connect"}</button>`
                    : step.actionKind === "refresh"
                      ? html`<button class="btn btn--sm" @click=${() => props.onRefreshConnectors()} ?disabled=${props.connectorsLoading || !props.connected}>${props.connectorsLoading ? "Checking..." : step.actionLabel ?? "Refresh"}</button>`
                      : step.href
                        ? html`<button class="btn btn--sm" @click=${() => {
                            if (step.id === "bcgpt" || step.id === "model-auth") { props.onNavigateTab("integrations"); return; }
                            if (step.id === "first-flow") { props.onNavigateTab("automations"); return; }
                            props.onNavigateTab("chat");
                          }}>${step.actionLabel ?? "Open"}</button>`
                        : nothing}
                </div>
              </div>
            `)}
          </div>
        </div>
      </details>

      <div class="card">
        <div class="card-title">Agent Timeline</div>
        <div class="card-sub">Live Wicked OS execution trace (model-agnostic schema).</div>
        ${trace.length
          ? html`
              <div class="list" style="margin-top: 12px;">
                ${trace.slice(0, 8).map(
                  (entry) => html`
                    <div class="list-item">
                      <div class="list-main">
                        <div class="list-title">${entry.title}</div>
                        <div class="list-sub">${entry.detail ?? `${entry.source}:${entry.kind}`}</div>
                      </div>
                      <div class="list-meta">
                        <span class=${traceStatusClass(entry.status)}>${entry.status}</span>
                        <span>${formatRelativeTimestamp(entry.ts)}</span>
                      </div>
                    </div>
                  `,
                )}
              </div>
            `
          : html`<div class="muted" style="margin-top: 12px;">No execution trace yet. Use Chat to start a run.</div>`}
        <div class="row" style="margin-top: 12px;">
          <button class="btn" @click=${() => props.onNavigateTab("chat")}>Open chat</button>
          <button class="btn btn--secondary" @click=${() => props.onClearTrace()} ?disabled=${trace.length === 0}>Clear trace</button>
        </div>
      </div>
    `;
  };

  const DASH_TABS: { key: "home" | "agents" | "workflows" | "system"; label: string }[] = [
    { key: "home", label: "Home" },
    { key: "agents", label: "Agents" },
    { key: "system", label: "System" },
  ];

  return html`
    <section class="dashboard-shell">
      <div class="dashboard-main">
    <!-- Greeting strip -->
    <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;">
      <div>
        <span style="font-size:20px;font-weight:600;">${getTimeGreeting()}</span>
        <span class="muted" style="font-size:13px;margin-left:10px;">Chat is live on the right &rarr;</span>
      </div>
      <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
        ${props.currentModel
          ? html`<span class="chip" style="font-size:11px;padding:4px 10px;">${props.currentModelProvider ?? "AI"} / ${props.currentModel?.includes("/") ? props.currentModel.split("/").pop() : props.currentModel}</span>`
          : html`<span class="chip chip-warn" style="font-size:11px;padding:4px 10px;">No model</span>`}
        <a href=${props.integrationsHref} class="btn btn--sm btn--secondary">Integrations</a>
      </div>
    </div>

    <!-- Tab strip -->
    <div class="dashboard-tab-strip">
      ${DASH_TABS.map(
        (t) => html`
          <button
            class="dashboard-tab-btn ${tab === t.key ? "active" : ""}"
            @click=${() => props.onDashboardTabChange(t.key)}
          >
            ${t.label}
          </button>
        `,
      )}
    </div>

    <!-- Tab content -->
    ${renderTabContent()}

      </div>

      <aside class="dashboard-side">
        <div class="card projects-chat-card dashboard-chat-card">
          <div class="dashboard-chat-card__head">
            <div>
              <div class="card-title">Workspace Chat</div>
              <div class="card-sub">Keep chat visible while you review health, runs, and connectors.</div>
            </div>
            <a href=${props.chatHref} style="font-size: 12px; opacity: 0.7;">Open full Chat</a>
          </div>
          <div class="row dashboard-chat-shortcuts" style="margin-top: 10px;">
            <button class="btn btn--sm" ?disabled=${!props.connected} @click=${() => props.onQuickAction("daily-report")}>
              Daily report
            </button>
            <button class="btn btn--sm" ?disabled=${!props.connected} @click=${() => props.onQuickAction("check-leads")}>
              Check leads
            </button>
            <button class="btn btn--sm" @click=${() => props.onQuickAction("create-workflow")}>
              Create workflow
            </button>
          </div>
          <div class="projects-chat-host dashboard-chat-host">
            ${renderChat(props.chatProps)}
          </div>
        </div>
      </aside>
    </section>
  `;
}
